import { DatePipe } from '@angular/common';
import { Component, EventEmitter, inject, Output } from '@angular/core';
import { AddMessageReplyData, AddParticipantData, ChangeParticipantPresence, Message, Participant } from '@lib/api/models/message';
import { ComponentsModule, DialogService, SnackBarService } from '@mastechnicaegroup/carelinelive-ui';
import { getDisplayName } from '@lib/api/models/person';
import { CreateMessageComponent } from '../create-message/create-message.component';
import { AddMessageReplyDialogComponent } from '../../../dialogs/reply/add/add-message-reply-dialog.component';
import { BehaviorSubject, catchError, of, take, tap } from 'rxjs';
import { AddParticipantDialogComponent } from '../../../dialogs/participants/add/add-participant-dialog.component';
import { selectedThread } from '@lib/messages/message.service';
import { AuthenticationService } from '@lib/auth/authentication.service';

@Component({
    selector: 'app-thread-messages',
    imports: [ComponentsModule, DatePipe],
    templateUrl: './thread-messages.component.html',
    standalone: true,
    styleUrl: './thread-messages.component.scss'
})

export class ThreadMessagesComponent {
    @Output() addRemoveParticipantEvent: EventEmitter<ChangeParticipantPresence> = new EventEmitter<ChangeParticipantPresence>();
    @Output() addReplyEvent: EventEmitter<void> = new EventEmitter<void>();

    private readonly _dialog = inject(DialogService);
    private readonly _authService = inject(AuthenticationService);
    protected readonly getDisplayName = getDisplayName;
    private readonly refreshTrigger$ = new BehaviorSubject<void>(undefined);
    private readonly _snackBar = inject(SnackBarService);

    protected readonly selectedThread = selectedThread;

    currentUserId: string | null = null;

    constructor() {
        this._authService.user$.subscribe(user => {
            if (user) {
                this.currentUserId = user.id;
            }
        });
    }

    createNewMessage() {
        return CreateMessageComponent.show(this._dialog).closed.pipe(
            tap(() => {
                this.refreshTrigger$.next();
                this.addReplyEvent.emit();
            }),
            take(1)
        ).subscribe();
    }

    onRemove(participant: Participant) {
        const participantPresence: ChangeParticipantPresence = {
            thread_id: selectedThread()!.id,
            participant_id: participant.user_id,
            removed: true
        };

        this.addRemoveParticipantEvent.emit(participantPresence);
    }

    addReply(data: AddMessageReplyData) {
        window.scrollTo(0, 0);
        return AddMessageReplyDialogComponent.show(this._dialog, data).closed.pipe(
            tap(() => {
                this.refreshTrigger$.next();
                this.addReplyEvent.emit();
            }),
            catchError(() => {
                this._snackBar.open({
                    colour: 'error',
                    dismissable: false,
                    icon: 'error_outline',
                    title: 'Failed to add message reply',
                    actions: [{ id: 'undo', label: 'OK' }]
                });
                return of(null);
            })
        ).subscribe();
    }

    addParticipant(data: AddParticipantData) {
        window.scrollTo(0, 0);
        return AddParticipantDialogComponent.show(this._dialog, data).closed.pipe(
            tap(() => {
                this.refreshTrigger$.next();
                this.addReplyEvent.emit();
            }),
            catchError(() => {
                this._snackBar.open({
                    colour: 'error',
                    dismissable: false,
                    icon: 'error_outline',
                    title: 'Failed to add participant',
                    actions: [{ id: 'undo', label: 'OK' }]
                });
                return of(null);
            })
        ).subscribe();
    }

    get sortedParticipants() {
        const participants = this.selectedThread()?.participants ?? [];
        const senderId = this.selectedThread()?.sender_id;

        return participants
            .filter(p => !p.removed)
            .sort((a, b) => {
                if (a.user_id === senderId) {
                    return -1;
                }
                if (b.user_id === senderId) {
                    return 1;
                }
                return 0;
            });
    }

    get isCurrentUserRemoved(): boolean {
        if (!this.currentUserId || !this.selectedThread()) {
            return false;
        }

        const currentUserParticipant = this.selectedThread()?.participants.find(p =>
            p.user_id === this.currentUserId);

        return currentUserParticipant ? currentUserParticipant.removed : false;
    }

    get visibleMessages(): Message[] {
        if (!this.selectedThread()) {
            return [];
        }

        const messages = this.selectedThread()!.messages;

        // Show all messages if the current user is not removed.
        if (!this.isCurrentUserRemoved) {
            return messages;
        }

        // User is removed, find when they were removed
        const currentUserParticipant = this.selectedThread()?.participants.find(p =>
            p.user_id === this.currentUserId);

        if (!currentUserParticipant) {
            return messages;
        }

        const removalTimestamp = new Date(currentUserParticipant.updated_at);

        // Only show messages created before the user was removed
        return messages.filter(message => {
            const messageTimestamp = new Date(message.created_at);
            return messageTimestamp <= removalTimestamp;
        });
    }

    get canReply(): boolean {
        return !this.isCurrentUserRemoved;
    }
}
